import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthImg } from "@/components/AuthImg";
import { NotesLog } from "@/components/NotesLog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AiResearchHistory } from "@/components/AiResearchHistory";
import { AiWorkbench } from "@/components/AiWorkbench";
import { MeasurementsEditor } from "@/components/MeasurementsEditor";
import { PartNumbersEditor } from "@/components/PartNumbersEditor";
import { PhotoUploader } from "@/components/PhotoUploader";
import { LockedField } from "@/components/LockedField";
import { IdentifyPickField } from "@/components/IdentifyPickField";
import { PhotoViewer } from "@/components/PhotoViewer";
import { KeywordChips } from "@/components/KeywordChips";
import { BackLink } from "@/components/BackLink";
import { leaveStep } from "@/lib/nav";
import { UnsavedWorkDialog } from "@/components/UnsavedWorkDialog";
import { useLeaveWarning } from "@/lib/useDraft";
import {
  Tag, ImageOff, ImagePlus, CheckCircle2, Loader2, ArrowLeft,
  Truck, PackagePlus, FileSpreadsheet, GitBranch, Boxes,
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, Lock,
  Circle, ShieldAlert, FlaskConical, DollarSign, RotateCw, Sparkles, Check, StickyNote, Pencil, Plus, Globe, CornerUpLeft, GraduationCap, Camera,
} from "lucide-react";
import { ScanTextInput } from "@/components/ScanTextInput";
import { RedTwinView } from "@/components/RedTwinView";

// Which form fields each AI suggestion owns — so Undo puts back exactly what it changed, including
// the part type a category drags with it.
const AI_FIELD_KEYS = {
  name: ["name"],
  description: ["description"],
  brand: ["brand"],
  category: ["category_id", "part_type_id"],
  part_type: ["part_type_id", "category_id"],
  oem_number: ["oem_number"],
  tag_serial: ["tag_serial"],
  value: ["internal_value", "value_basis"],
  market_value: ["internal_value", "value_basis"],
};


const SOURCE_META = {
  donor: { label: "Donor", icon: Truck, cls: "bg-blue-100 text-blue-700" },
  field: { label: "Field lot", icon: PackagePlus, cls: "bg-amber-100 text-amber-700" },
  import: { label: "Import", icon: FileSpreadsheet, cls: "bg-violet-100 text-violet-700" },
  split: { label: "Split", icon: GitBranch, cls: "bg-teal-100 text-teal-700" },
  manual: { label: "Manual", icon: Boxes, cls: "bg-slate-100 text-slate-600" },
};

// Confidence tier badge — the "solid vs guessed" signal, mostly auto-set.
// ONE combined price stamp = the confidence ladder. Owner is always the staff who verified
// it; the assisting tool is folded into the same label. Researched is the gold rung and
// wins regardless of which tool got it there.
function priceStamp(valueSource, sourceTool) {
  if (valueSource === "researched") return { label: "Researched \u2713", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: "check" };
  if (valueSource === "verified_transaction") return { label: "Verified at sale", cls: "bg-teal-50 text-teal-800 border-teal-200", icon: null };
  if (sourceTool === "ai") return { label: "Staff estimate \u00b7 AI assist", cls: "bg-violet-50 text-violet-700 border-violet-200", icon: "sparkles" };
  if (sourceTool === "perplexity") return { label: "Staff estimate \u00b7 Perplexity market analysis", cls: "bg-sky-50 text-sky-700 border-sky-200", icon: "globe" };
  return { label: "Staff estimate", cls: "bg-blue-50 text-blue-700 border-blue-200", icon: null };
}



// Damage/defect flags that require a photo before finalizing (mirrors backend DAMAGE_FLAGS).
const DAMAGE_FLAGS = ["damaged", "cracked", "corrosion", "missing-components"];

const FieldLabel = ({ children, accent }) => (
  <Label className={`text-xs font-bold uppercase tracking-wider ${accent ? "text-[#B45309]" : "text-slate-500"}`}>{children}</Label>
);

const Section = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5">
    <h3 className="font-display text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">{title}</h3>
    {children}
  </div>
);

export default function SortIdentifyDetailPage() {
  const { partId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { has } = useAuth();
  const canFinalize = has("finalize_parts");
  const directFinalize = has("finalize_without_review");
  const canReview = has("review_parts");
  // Where to return after an action — honor the origin (e.g. Admin Review), else the queue.
  const returnTo = location.state?.from || "/sort-queue";
  const returnLabel = location.state?.fromLabel || "Queue";
  const canQuickAdd = has("quick_add_taxonomy") || has("manage_taxonomy");
  const canAddListValue = canQuickAdd || has("manage_lists");
  const finalizeLabel = directFinalize ? "Finalize" : "Complete & send to review";

  const [part, setPart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tax, setTax] = useState({ categories: [], part_types: [] });
  const [constants, setConstants] = useState({ grades: [], condition_flags: [] });
  const [gate, setGate] = useState({ minor_mode: "light", major_value_threshold: null });
  const [valueEdited, setValueEdited] = useState(false);
  const [valueStamp, setValueStamp] = useState(null);   // null | "estimate" | "market" (AI value provenance)
  const [flashField, setFlashField] = useState(null);   // brief highlight after an AI "Use"
  const flashTimer = useRef(null);
  const flashNow = (k) => {
    setFlashField(k);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashField((c) => (c === k ? null : c)), 1500);
  };
  const flashCls = (k) => (flashField === k ? " ring-2 ring-[#E0781A] ring-offset-1 transition-shadow" : "");
  const [form, setForm] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [signingOff, setSigningOff] = useState(false);
  const [reviewNoteOpen, setReviewNoteOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [teachNote, setTeachNote] = useState("");
  const [showDismantler, setShowDismantler] = useState(false);
  const [aiFields, setAiFields] = useState(() => new Set()); // fields filled by AI, still unverified
  // What the AI actually READ for an identity field (confidence / raw read / unsure characters),
  // kept so it is saved on the part and not just shown once on the Ask AI panel.
  const [aiReads, setAiReads] = useState({});
  // What each field held BEFORE the AI filled it — so every AI click can be taken back out.
  const preAi = useRef({});
  const [twinFor, setTwinFor] = useState(null);   // red twin view: the number being compared
  const [suggest, setSuggest] = useState({ brands: [], names: [] });
  const [veh, setVeh] = useState({ years: [], makes: [], models: [], engines: [] });

  // THE CHIP IS THE ONLY VERIFY (his instruction, 2026-10-08). Clicking ✓verify means THE AI WAS
  // RIGHT — a person has checked it — so the field is recorded as verified by them. CHANGING the
  // value means the AI was wrong, and that must never read as a verification, so any edit drops
  // the stamp again. Nothing about the chip changes on screen.
  const [personVerified, setPersonVerified] = useState({});
  // TWO ANSWERS, NOT THREE — he removed "can't tell" on 2026-10-13 and gave the reason: the rule
  // is that nothing moves forward without ✓verify, and a "can't tell" button dilutes it. *"If they
  // can't read it, they don't verify it."* An unreadable stamp is simply an unverified field, which
  // already blocks the part. Right and wrong are both in what gets saved: the characters either
  // stayed the AI's or they didn't.
  const clearAiField = (field) => {
    setPersonVerified((v) => { if (!(field in v)) return v; const n = { ...v }; delete n[field]; return n; });
    setAiFields((s) => { if (!s.has(field)) return s; const n = new Set(s); n.delete(field); return n; });
  };
  const verifyAiField = (field) => {
    clearAiField(field);
    setPersonVerified((v) => ({ ...v, [field]: true }));
    if (field === "value") setValueStamp(null);
  };

  const logNearMiss = useCallback((payload) => { api.post("/taxonomy/near-miss", payload).catch(() => {}); }, []);

  const loadLists = useCallback(() => {
    Promise.all([api.get("/managed-lists/brands"), api.get("/managed-lists/names")])
      .then(([b, n]) => setSuggest({
        brands: (b.data.items || []).map((x) => x.name),
        names: (n.data.items || []).map((x) => x.name),
      })).catch(() => {});
  }, []);

  // Vehicle managed lists (2e) — flat years/makes + cascading models (by make) / engines (by model).
  const loadVehYears = useCallback(() => api.get("/managed-lists/vehicle/years").then(({ data }) => setVeh((v) => ({ ...v, years: data.items || [] }))).catch(() => {}), []);
  const loadVehMakes = useCallback(() => api.get("/managed-lists/vehicle/makes").then(({ data }) => setVeh((v) => ({ ...v, makes: data.items || [] }))).catch(() => {}), []);
  const reloadVehModels = useCallback((mid) => { if (!mid) return; api.get(`/managed-lists/vehicle/models?make_id=${mid}`).then(({ data }) => setVeh((v) => ({ ...v, models: data.items || [] }))).catch(() => {}); }, []);
  const reloadVehEngines = useCallback((mid) => { if (!mid) return; api.get(`/managed-lists/vehicle/engines?model_id=${mid}`).then(({ data }) => setVeh((v) => ({ ...v, engines: data.items || [] }))).catch(() => {}); }, []);

  const loadTax = useCallback(() => api.get("/taxonomy").then(({ data }) => setTax(data)).catch(() => {}), []);

  const loadPart = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sort-queue/${partId}`);
      setPart(data);
      setForm({
        name: data.name || "", brand: data.brand || "", category_id: data.category_id || "",
        part_type_id: data.part_type_id || "", oem_number: data.oem_number || "", tag_serial: data.tag_serial || "",
        superseded: (data.superseded_numbers || []).join(", "), grade: data.grade || "",
        keywords: data.keywords || [],
        keyword_sources: data.keyword_sources || [],
        condition_flags: data.condition_flags || [], retail_price: data.retail_price ?? "", cost: data.cost ?? "",
        internal_value: data.internal_value ?? "", value_basis: data.value_basis || "",
        value_source: data.value_source || "",
        description: data.description || "",
        reason: "",
        year: data.year ?? "", make: data.make || "", model: data.model || "", engine: data.engine || "",
        unknown_unverified: !!data.unknown_unverified,
      });
      setAiFields(new Set(data.ai_estimated_fields || []));
      setPhotos((data.photos || []).filter((p) => !p.is_archived));
      savedAt.current = null;   // re-snapshotted on the next render, once `form` is in state
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load this part.");
      navigate("/sort-queue");
    } finally { setLoading(false); }
  }, [partId, navigate]);

  useEffect(() => { loadPart(); }, [loadPart]);

  // Auto-OPEN the dismantler notes panel when the lot carries notes, so the identifier
  // can't miss them while naming the part (they must actively close it). Runs once per part.
  const autoOpenedFor = useRef(null);
  useEffect(() => {
    const lotId = part?.lot?.id;
    if (lotId && (part?.lot?.notes_count || 0) > 0 && autoOpenedFor.current !== lotId) {
      autoOpenedFor.current = lotId;
      setShowDismantler(true);
    }
  }, [part]);

  // Lightweight refresh after an AI number-add: updates part.numbers + photos WITHOUT
  // flipping the page into its loading state (keeps the AI result tray mounted) and
  // WITHOUT clobbering unsaved form edits.
  // LEAVING THIS PAGE WITH UNSAVED EDITS ASKS FIRST (2026-10-15, after a phone lost a yard's worth
  // of work on a different screen). This is the longest form in the app and the one the crew uses
  // most, so Back, refresh and closing the tab all stop and ask instead of quietly dropping it.
  const savedAt = useRef(null);
  const formJson = JSON.stringify(form);
  useEffect(() => { if (savedAt.current === null && part) savedAt.current = formJson; }, [formJson, part]);
  const dirty = !!part && savedAt.current !== null && savedAt.current !== formJson;
  const [askLeave, setAskLeave] = useState(false);
  const leaveRef = useRef(null);
  useLeaveWarning(dirty);
  const guardLeave = (go) => { if (!dirty) { go(); return; } leaveRef.current = go; setAskLeave(true); };

  const refreshPart = useCallback(async () => {
    try {
      const { data } = await api.get(`/sort-queue/${partId}`);
      setPart(data);
      setPhotos((data.photos || []).filter((p) => !p.is_archived));
    } catch { /* silent */ }
  }, [partId]);
  useEffect(() => {
    api.get("/taxonomy").then(({ data }) => setTax(data)).catch(() => {});
    api.get("/inventory/constants").then(({ data }) => setConstants(data)).catch(() => {});
    api.get("/inventory/finalize-gate").then(({ data }) => setGate(data)).catch(() => {});
    loadLists();
    loadVehYears();
    loadVehMakes();
  }, [loadLists, loadVehYears, loadVehMakes]);

  // Cascade: derive the selected make/model ids from the stored strings, then fetch
  // the child list. Clearing a parent empties the child list.
  const nmv = (s) => (s || "").trim().toLowerCase();
  const makeId = useMemo(() => { const t = nmv(form?.make); return t ? (veh.makes.find((m) => nmv(m.name) === t)?.id || "") : ""; }, [form?.make, veh.makes]);
  const modelId = useMemo(() => { const t = nmv(form?.model); return t ? (veh.models.find((m) => nmv(m.name) === t)?.id || "") : ""; }, [form?.model, veh.models]);
  useEffect(() => { if (!makeId) { setVeh((v) => ({ ...v, models: [] })); return; } reloadVehModels(makeId); }, [makeId, reloadVehModels]);
  useEffect(() => { if (!modelId) { setVeh((v) => ({ ...v, engines: [] })); return; } reloadVehEngines(modelId); }, [modelId, reloadVehEngines]);
  const yearOpts = useMemo(() => veh.years.map((x) => ({ value: x.name, label: x.name })), [veh.years]);
  const makeOpts = useMemo(() => veh.makes.map((x) => ({ value: x.name, label: x.name })), [veh.makes]);
  const modelOpts = useMemo(() => veh.models.map((x) => ({ value: x.name, label: x.name })), [veh.models]);
  const engineOpts = useMemo(() => veh.engines.map((x) => ({ value: x.name, label: x.name })), [veh.engines]);

  // Pass 2: apply an AI suggestion into the form as an AI-estimate (unverified). Never
  // auto-saves, never creates taxonomy. Category/part-type match EXISTING lists only.
  const matchTax = (list, text) => {
    const t = (text || "").trim().toLowerCase();
    if (!t) return null;
    return list.find((x) => (x.name || "").toLowerCase() === t)
      || list.find((x) => (x.name || "").toLowerCase().includes(t) || t.includes((x.name || "").toLowerCase()))
      || null;
  };
  // Strict list match for the managed Brand / Name lists — returns the canonical string
  // (exact-normalized wins) or null. No silent free-text: AI-apply must hit an existing entry.
  const matchListName = (names, text) => {
    const t = (text || "").trim().toLowerCase();
    if (!t) return null;
    return (names || []).find((n) => (n || "").toLowerCase() === t)
      || (names || []).find((n) => (n || "").toLowerCase().includes(t) || t.includes((n || "").toLowerCase()))
      || null;
  };
  // meta (optional) = what the AI actually read for an identity field: confidence, the raw read
  // and the unsure characters. It rides along so the read is saved ON the part instead of living
  // only in the AI log — it never changes the value and it never verifies anything.
  const applyAiSuggestion = (field, value, meta = null) => {
    // UNDO ON EVERY CLICK (his instruction, 2026-10-17). What the field held BEFORE the AI touched
    // it is kept, once, so one tap puts it back exactly as it was — including the fields a category
    // drags with it.
    if (!(field in preAi.current)) {
      const keys = AI_FIELD_KEYS[field] || [];
      preAi.current[field] = Object.fromEntries(keys.map((k) => [k, form[k]]));
    }
    const ok = _applyAiCore(field, value);
    if (ok) {
      flashNow(field === "market_value" ? "value" : field);
      if (meta && (field === "oem_number" || field === "tag_serial")) {
        setAiReads((s) => ({ ...s, [field]: meta }));
      }
    } else {
      delete preAi.current[field];
    }
    return ok;
  };

  // ONE TAP TAKES IT BACK OUT — the form returns to exactly what it held before the AI filled it.
  const undoAiSuggestion = (field) => {
    const snap = preAi.current[field];
    if (!snap) return false;
    setForm((s) => ({ ...s, ...snap }));
    delete preAi.current[field];
    const f = field === "market_value" ? "value" : field;
    clearAiField(f);
    setAiReads((s) => { if (!(field in s)) return s; const n = { ...s }; delete n[field]; return n; });
    if (f === "value") { setValueStamp(null); setValueEdited(false); }
    if (f === "category") clearAiField("part_type");
    return true;
  };

  // A SEARCH WORD CAN BE TAKEN BACK OUT TOO, with its owner record.
  const aiRemoveKeyword = (value) => {
    const v = String(value || "").trim().toLowerCase();
    if (!v) return;
    setForm((s) => ({
      ...s,
      keywords: (s.keywords || []).filter((k) => String(k).toLowerCase() !== v),
      keyword_sources: (s.keyword_sources || []).filter((x) => String(x.kw).toLowerCase() !== v),
    }));
  };

  const _applyAiCore = (field, value) => {
    if (field === "name") {
      const hit = matchListName(suggest.names, value);
      if (!hit) { toast.error(`"${value}" isn't in the Name list yet — use +New (or "Add to list") to add it.`); return false; }
      setForm((s) => ({ ...s, name: hit }));
      setAiFields((s) => new Set(s).add("name"));
      return true;
    }
    if (field === "description") {
      setForm((s) => ({ ...s, description: value }));
      setAiFields((s) => new Set(s).add("description"));
      return true;
    }
    if (field === "brand") {
      const hit = matchListName(suggest.brands, value);
      if (!hit) { toast.error(`"${value}" isn't in the Brand list yet — use +New (or "Add to list") to add it.`); return false; }
      setForm((s) => ({ ...s, brand: hit }));
      setAiFields((s) => new Set(s).add("brand"));
      return true;
    }
    if (field === "category") {
      const hit = matchTax(tax.categories || [], value);
      if (!hit) { toast.error(`No matching category for "${value}" — use +New to add it.`); return false; }
      setForm((s) => ({ ...s, category_id: hit.id, part_type_id: "" }));
      setAiFields((s) => { const n = new Set(s); n.add("category"); n.delete("part_type"); return n; });
      return true;
    }
    if (field === "part_type") {
      const hit = matchTax(tax.part_types || [], value);
      if (!hit) { toast.error(`No matching part type for "${value}" — pick a category, then +New to add it.`); return false; }
      setForm((s) => ({ ...s, part_type_id: hit.id, category_id: hit.category_id || s.category_id }));
      setAiFields((s) => new Set(s).add("part_type"));
      return true;
    }
    if (field === "oem_number") {
      setForm((s) => ({ ...s, oem_number: value }));
      setAiFields((s) => new Set(s).add("oem_number"));
      return true;
    }
    if (field === "tag_serial") {
      setForm((s) => ({ ...s, tag_serial: value }));
      setAiFields((s) => new Set(s).add("tag_serial"));
      return true;
    }
    if (field === "value" || field === "market_value") {
      const num = Number(value);
      if (!Number.isFinite(num) || num < 0) return false;
      const isMarket = field === "market_value";
      setForm((s) => ({ ...s, internal_value: String(num), value_basis: isMarket ? (s.value_basis || "used") : s.value_basis }));
      setValueEdited(false);
      setValueStamp(isMarket ? "market" : "estimate");
      setAiFields((s) => new Set(s).add("value"));
      return true;
    }
    return false;
  };

  // Inline "create list value on the spot" for the AI Workbench — reuses the SAME managed
  // taxonomy backend as the +New pickers. Explicit user click only; never silent.
  const aiCreateTaxonomy = async (kind, name) => {
    try {
      if (kind === "name" || kind === "brand") {
        const listKey = kind === "name" ? "names" : "brands";
        const { data } = await api.post(`/managed-lists/${listKey}`, { name });
        loadLists();
        setForm((s) => ({ ...s, [kind]: data.item.name }));
        setAiFields((s) => new Set(s).add(kind));
        return true;
      }
      if (kind === "category") {
        const { data } = await api.post("/taxonomy/categories", { name });
        await loadTax();
        setForm((s) => ({ ...s, category_id: data.id, part_type_id: "" }));
        setAiFields((s) => { const n = new Set(s); n.add("category"); n.delete("part_type"); return n; });
        return true;
      }
      if (kind === "part_type") {
        if (!form.category_id) { toast.error("Pick a category first, then add the part type."); return false; }
        const { data } = await api.post("/taxonomy/part-types", { name, category_id: form.category_id });
        await loadTax();
        setForm((s) => ({ ...s, part_type_id: data.id }));
        setAiFields((s) => new Set(s).add("part_type"));
        return true;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add to the list.");
    }
    return false;
  };

  // Keywords are FREE (ungated) — appended directly, never stamped unverified.
  // A SEARCH WORD BELONGS TO SOMETHING (his instruction, 2026-10-14): to the assembly, or to one
  // bolt-on component's tag. The word itself joins the one flat list — search is unchanged — and
  // `scope` records the owner, so a component's words are offered to travel with it when it is
  // childed off while the assembly's own words stay put.
  const aiAddKeywords = (vals, scope = null) => {
    const add = (Array.isArray(vals) ? vals : [vals]).map((x) => String(x).trim()).filter(Boolean);
    if (!add.length) return;
    setForm((s) => {
      const merged = [...(s.keywords || [])];
      add.forEach((k) => { if (!merged.some((x) => x.toLowerCase() === k.toLowerCase()) && merged.length < 25) merged.push(k.slice(0, 40)); });
      const srcs = [...(s.keyword_sources || [])];
      if (scope?.group_id) {
        add.forEach((k) => {
          if (!merged.some((x) => x.toLowerCase() === k.toLowerCase())) return;
          if (srcs.some((x) => String(x.kw).toLowerCase() === k.toLowerCase())) return;
          srcs.push({ kw: k.slice(0, 40), group_id: scope.group_id, group_label: scope.group_label || null });
        });
      }
      return { ...s, keywords: merged, keyword_sources: srcs };
    });
  };

  // THE RED "CHECK THE PHOTO" STAMP STAYS UNTIL A PERSON VERIFIES IT (his instruction,
  // 2026-10-09). It used to live only inside the Ask-AI panel, so a read the AI itself called
  // poor arrived on the part looking like any other value. It now rides with the read (live from
  // the panel, and from the part after a reload) and only the ✓verify chip takes it off. A read
  // whose characters have since been changed is no longer the value on screen, so it drops out.
  const needsRetake = (field) => {
    if (personVerified[field]) return false;
    if (aiReads[field]?.needs_retake) return true;
    const saved = part?.ai_reads?.[field];
    if (!saved?.needs_retake || saved?.verified_at) return false;
    return String(saved.value || "").trim().toUpperCase() === String(form?.[field] || "").trim().toUpperCase();
  };

  // THE ORANGE "UNCLEAR CHARS" LINE, ON EVERY NUMBER IT APPLIES TO (his instruction,
  // 2026-10-13): *"I LIKE the orange 'unclear chars' line... Don't reduce it, hide it or reword
  // it."* It was already under each candidate in the Ask-AI panel, and on each saved number row —
  // but NOT under the identity fields the read was inserted into, which is where his crew types.
  // Same words as the panel, on purpose.
  const unclearLine = (field) => {
    const chars = aiReads[field]?.unsure_chars?.length
      ? aiReads[field].unsure_chars
      : ((part?.ai_reads?.[field]?.value || "").trim().toUpperCase() === String(form?.[field] || "").trim().toUpperCase()
        ? (part?.ai_reads?.[field]?.unsure_chars || []) : []);
    if (!chars.length) return null;
    return (
      <div data-testid={`ai-unclear-chars-${field}`} className="text-[11px] text-amber-700 mt-1.5">
        Unclear chars: {chars.join(", ")} — verify against the physical stamp.
      </div>
    );
  };

  // WHY it is red, in his crew's words — "we already hold 119045 on FAS-003262 — one character
  // different". Red that cannot say what it nearly matched is just a scold.
  const retakeReason = (field) =>
    aiReads[field]?.retake_reason || part?.ai_reads?.[field]?.retake_reason || "";

  const aiBadge = (field) => {
    if (!aiFields.has(field)) return null;
    const isNum = field === "oem_number" || field === "tag_serial";
    const label = field === "value"
      ? (valueStamp === "market" ? "AI Assist — market — unverified" : "AI Assist — unverified · ESTIMATE")
      : (isNum ? "AI-inserted · unverified" : "AI-estimate · unverified");
    return (
      <span data-testid={`ai-estimate-badge-${field}`} className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5">
        <Sparkles className="w-2.5 h-2.5" />{label}
        {needsRetake(field) && (
          <span data-testid={`ai-needs-retake-${field}`} title={retakeReason(field) || "The AI said this read was poor — look at the photo and the tag before you verify it"}
            className="inline-flex items-center gap-0.5 text-[#B01E28] bg-red-50 border border-red-200 rounded px-1 py-0.5">
            <Camera className="w-2.5 h-2.5" />check the photo
          </span>
        )}
        {/* ONE TAP SETTLES IT (his instruction, 2026-10-13). Only on the number fields, because only
            a number can have a look-alike on the shelves. */}
        {needsRetake(field) && isNum && (form?.[field] || "").trim() && (
          <button type="button" data-testid={`ai-see-twin-${field}`} onClick={() => setTwinFor(String(form[field]).trim())}
            className="inline-flex items-center text-[#B01E28] underline underline-offset-2 px-1 py-2 -my-2 font-bold">
            see both
          </button>
        )}
        {/* ONE WORDING EVERYWHERE — his instruction, 2026-10-13: *"one wording everywhere —
            '✓verify', the one my crew knows."* The number fields used to read "I checked this
            against the tag", which made one control look like two different buttons. It reads
            small, as his crew knows it, but the tap area is padded out to a gloved-thumb target. */}
        <button type="button" data-testid={`ai-verify-${field}`} onClick={() => verifyAiField(field)} title="Confirm — clears the unverified flag" className="ml-0.5 inline-flex items-center text-emerald-600 hover:text-emerald-800 px-1.5 py-2 -my-2 -mx-0.5"><Check className="w-2.5 h-2.5" />verify</button>
      </span>
    );
  };

  const typesForCat = useMemo(
    () => (tax.part_types || []).filter((t) => !form?.category_id || t.category_id === form.category_id),
    [tax, form?.category_id]);
  const isMajor = useMemo(() => tax.part_types?.find((t) => t.id === form?.part_type_id)?.is_major, [tax, form?.part_type_id]);
  const nameOpts = useMemo(() => (suggest.names || []).map((n) => ({ value: n, label: n })), [suggest.names]);
  const brandOpts = useMemo(() => (suggest.brands || []).map((b) => ({ value: b, label: b })), [suggest.brands]);
  const catOpts = useMemo(() => (tax.categories || []).map((c) => ({ value: c.id, label: c.name })), [tax.categories]);
  const typeOpts = useMemo(() => typesForCat.map((t) => ({ value: t.id, label: `${t.name}${t.is_major ? " (major)" : ""}` })), [typesForCat]);

  if (loading || !part || !form) {
    return <div data-testid="identify-detail-loading" className="p-16 text-center font-mono text-xs text-slate-400">LOADING PART…</div>;
  }
  const reviewMode = part.status === "pending_review" && canReview;

  const meta = SOURCE_META[part.source_type] || SOURCE_META.manual;
  const SIcon = meta.icon;
  const toggleFlag = (f) => setForm((s) => ({ ...s, condition_flags: s.condition_flags.includes(f) ? s.condition_flags.filter((x) => x !== f) : [...s.condition_flags, f] }));

  // ---- Effective-major (incl. backstop) + live readiness for the finalize gate ----
  const catMajorBearing = (tax.part_types || []).some((t) => t.category_id === form.category_id && t.is_major);
  const valNum = form.internal_value === "" ? null : Number(form.internal_value);
  const thr = gate.major_value_threshold;
  const effectiveMajor = !!isMajor
    || (!form.part_type_id && !!form.category_id && catMajorBearing)
    || (thr != null && valNum != null && valNum >= thr);
  const fullGate = effectiveMajor || gate.minor_mode === "gated";
  const hasPhoto = photos.length > 0;
  const damage = form.condition_flags.some((f) => DAMAGE_FLAGS.includes(f));
  // his rule, 27 Oct: nothing finishes without a main photo a PERSON picked, still live (the server checks the same)
  const mainPicked = !!part.main_photo_file_id && part.main_photo?.file_id === part.main_photo_file_id
    && photos.some((p) => p.file_id === part.main_photo_file_id);
  const readinessItems = [
    { label: "Main photo", ok: mainPicked },
    { label: "Name", ok: !!form.name },
    { label: "Brand", ok: !!form.brand },
    { label: "Value", ok: valNum != null },
  ];
  if (fullGate) {
    readinessItems.push({ label: "Grade", ok: !!form.grade });
    readinessItems.push({ label: "Basis", ok: !!form.value_basis });
  }
  if (effectiveMajor && !form.unknown_unverified) {
    if (!form.part_type_id) readinessItems.push({ label: "Part type (looks major)", ok: false });
    const mk = part.donor_id ? part.donor?.make : form.make;
    const md = part.donor_id ? part.donor?.model : form.model;
    readinessItems.push({ label: "Make", ok: !!mk });
    readinessItems.push({ label: "Model", ok: !!md });
    readinessItems.push({ label: "Serial / tag", ok: !!form.tag_serial });
    readinessItems.push({ label: "Tag photo", ok: hasPhoto });
  }
  if (damage) readinessItems.push({ label: "Photo (damage flag)", ok: hasPhoto });
  const missing = readinessItems.filter((r) => !r.ok).map((r) => r.label);
  const ready = missing.length === 0;
  // Live confidence tier for the badge — mirrors what the backend stores on save. The
  // moment the value is edited it's a plain staff estimate; an earned tier
  // (researched / market_perplexity) only shows when the stored value stands behind it.
  const displaySource = (valueEdited && valNum != null) ? "staff_estimate" : form.value_source;
  // SEPARATE tool dimension for the badge: an AI apply stamps "ai"; a manual edit clears it;
  // an untouched value keeps whatever informed it (e.g. a prior Perplexity pull).
  const displayTool = valueStamp ? "ai" : (valueEdited ? null : part.source_tool);

  const body = () => ({
    name: form.name, brand: form.brand, category_id: form.category_id || null, part_type_id: form.part_type_id || null,
    oem_number: form.oem_number, tag_serial: form.tag_serial,
    year: form.year === "" ? null : String(form.year), make: form.make || null, model: form.model || null, engine: form.engine || null,
    superseded_numbers: form.superseded ? form.superseded.split(",").map((x) => x.trim()).filter(Boolean) : [],
    keywords: form.keywords || [],
    keyword_sources: form.keyword_sources || [],
    description: form.description || "",
    grade: form.grade || null, condition_flags: form.condition_flags,
    condition_override_reason: (form.condition_override_reason || "").trim() || undefined,
    internal_value: form.internal_value === "" ? null : Number(form.internal_value),
    value_basis: form.value_basis || null, value_source: form.value_source || null,
    source_tool: valueStamp ? "ai" : null,
    reason: (form.reason || "").trim() || undefined,
    ai_estimated_fields: Array.from(aiFields),
    ai_unverified: aiFields.size > 0,
    ai_reads: Object.keys(aiReads).length ? aiReads : undefined,
    verified_fields: Object.keys(personVerified).length ? Object.keys(personVerified) : undefined,
  });

  // Protected-field change detection (mirrors the Part edit panel + the backend
  // _detect_protected_changes trigger): only fires when an EXISTING, non-empty OEM #,
  // supersession #, or internal value/basis is being CHANGED — never on first-time
  // entry into a blank field, never on a no-op re-save.
  const oemChanged = !!(part.oem_number || "").trim() && (form.oem_number || "").trim() !== (part.oem_number || "").trim();
  const oldSup = (part.superseded_numbers || []).map((x) => String(x).trim()).filter(Boolean);
  const newSup = (form.superseded || "").split(",").map((x) => x.trim()).filter(Boolean);
  const supersededChanged = oldSup.length > 0 && JSON.stringify(oldSup) !== JSON.stringify(newSup);
  const valueChanged = part.internal_value != null && valNum != null && valNum !== part.internal_value;
  const basisChanged = part.internal_value != null && !!part.value_basis && !!form.value_basis && form.value_basis !== part.value_basis;
  const protectedChange = oemChanged || supersededChanged || valueChanged || basisChanged;

  const saveDetails = async () => {
    if (protectedChange && !(form.reason || "").trim()) {
      toast.error("A reason is required to change a protected field (OEM #, supersession, or value)."); return;
    }
    setBusy(true);
    try { await api.put(`/parts/${part.id}`, body()); savedAt.current = JSON.stringify(form); toast.success("Details saved"); loadPart(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // "Researched" is EARNED by an explicit human sign-off (name + timestamp) on the SAVED
  // value — no baseless flip. Save the value first, then sign off.
  const signOffResearched = async () => {
    if (valueEdited) { toast.error("Save the value first, then sign off."); return; }
    if (valNum == null) { toast.error("Set an internal value before signing off."); return; }
    setSigningOff(true);
    try { await api.post(`/parts/${part.id}/price/signoff`, {}); toast.success("Signed off as researched"); loadPart(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSigningOff(false); }
  };

  // Apply a REAL Perplexity market pull → 'market_perplexity' (median + citations read
  // server-side from the logged research return; the badge can't lie about it).
  const applyMarketPrice = async (returnId) => {
    if (!returnId) { toast.error("No market result to apply."); return false; }
    try {
      await api.post(`/parts/${part.id}/price/market`, { return_id: returnId, basis: form.value_basis || "used" });
      toast.success("Applied market price (Perplexity)"); loadPart(); return true;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
  };

  const finalize = async (sendToReview = false) => {
    if (!form.name || !form.brand) { toast.error("Name and brand are required before finalizing."); return; }
    if (!ready) { toast.error(`Not ready — still need: ${missing.join(", ")}`); return; }
    if (!part.donor_id && !(form.year && form.make && form.model)
        && !window.confirm("No vehicle info (year/make/model) selected. Finalize anyway?")) return;
    setBusy(true);
    let saved = false;
    try {
      await api.put(`/parts/${part.id}`, body());
      saved = true; savedAt.current = JSON.stringify(form);
      const res = await api.post(`/parts/${part.id}/finalize`, {
        grade: form.grade || null,
        condition_flags: form.condition_flags,
        internal_value: Number(form.internal_value), value_basis: form.value_basis || null,
        value_source: form.value_source || null,
        unknown_unverified: form.unknown_unverified,
        send_to_review: sendToReview,
      });
      toast.success(res.data?.sent_to_review ? `${part.short_code} completed — sent to Admin Review` : `${part.short_code} is now Available`);
      leaveStep(navigate, location, returnTo);   // Edit and Finish are not pages (29 Oct)
    } catch (e) { refused(e, saved, "Not finished"); }
    finally { setBusy(false); }
  };

  // ADMIN REVIEW actions (visible only when reviewing a pending_review part). Fix-&-approve
  // saves the admin's edits first so production keeps moving; approve/return go back to the
  // Admin Review queue (never dumps to the Identify queue).
  const approveReview = async (fixFirst) => {
    setBusy(true);
    let saved = false;
    try {
      if (fixFirst) { await api.put(`/parts/${part.id}`, body()); saved = true; savedAt.current = JSON.stringify(form); }
      await api.post(`/parts/${part.id}/review/approve`, { note: teachNote.trim() });
      toast.success(`${part.short_code} approved → Available`);
      leaveStep(navigate, location, returnTo === "/sort-queue" ? "/review-queue" : returnTo);
    } catch (e) { refused(e, saved, "Not approved"); }
    finally { setBusy(false); }
  };
  // his 28 Oct ask: when Finish or Approve is refused AFTER the edits went in, say plainly they were kept
  const refused = (e, saved, what) => {
    const why = formatApiErrorDetail(e.response?.data?.detail);
    if (!saved) { toast.error(why); return; }
    toast.error(`${what} — ${why}`, { description: "Your edits were saved. Nothing was lost.", duration: 9000 });
    loadPart();
  };
  const returnReview = async () => {
    if (!reviewNote.trim()) { toast.error("Add a feedback note so the employee knows what to fix."); return; }
    setBusy(true);
    try {
      await api.post(`/parts/${part.id}/review/return`, { note: reviewNote.trim() });
      toast.success(`${part.short_code} returned for rework`);
      leaveStep(navigate, location, returnTo === "/sort-queue" ? "/review-queue" : returnTo);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); setReviewNoteOpen(false); }
  };

  const addIdentifyPhoto = (photo) => { if (photo?.file_id) setPhotos((p) => [...p, photo]); };

  return (
    <div data-testid="identify-detail-page" className="pb-10">
      {/* Sticky header — back nav + identity + source */}
      <div className="sticky top-0 z-10 -mx-4 px-4 py-3 bg-[#F7F6F4]/95 backdrop-blur border-b border-slate-200 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <BackLink to={returnTo} label={returnLabel} testid="identify-back"
            onClick={() => guardLeave(() => leaveStep(navigate, location, returnTo))} />
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <Tag className="w-6 h-6 text-[#B01E28]" /><span className="font-mono">{part.short_code}</span>
          </h1>
          <Badge data-testid="identify-source-context" className={`rounded-full border-0 text-[11px] font-mono ${meta.cls}`}><SIcon className="w-3 h-3 mr-1" />{meta.label}</Badge>
          {part.status === "pending_review" && <Badge data-testid="identify-pending-review" className="rounded-full border-0 text-[11px] bg-amber-100 text-amber-800">Awaiting review</Badge>}
          <span className="text-sm text-slate-500 font-mono truncate">{part.source_label || "—"}</span>
          <div className="ml-auto flex gap-2">
            {part.status === "pending_review" && canReview ? (
              <>
                <Button data-testid="review-return-btn" variant="outline" disabled={busy} onClick={() => { setReviewNote(""); setReviewNoteOpen(true); }} className="rounded-lg h-10 border-red-300 text-red-700 hover:bg-red-50">
                  <CornerUpLeft className="w-4 h-4 mr-1.5" />Return for rework
                </Button>
                <Button data-testid="review-approve-btn" disabled={busy} onClick={() => approveReview(true)} className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white h-10 px-5">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" />Fix &amp; approve → Available</>}
                </Button>
              </>
            ) : (
              <>
                <Button data-testid="identify-save" variant="outline" disabled={busy} onClick={saveDetails} className="rounded-lg h-10">Save details</Button>
                {canFinalize && directFinalize && (
                  <Button data-testid="identify-send-review" variant="outline" disabled={busy || !ready} onClick={() => finalize(true)} className="rounded-lg h-10 border-amber-300 text-amber-700 hover:bg-amber-50" title="Not sure? Send it for a second set of eyes.">
                    <ShieldAlert className="w-4 h-4 mr-1.5" />Check me
                  </Button>
                )}
                {canFinalize && (
                  <Button data-testid="identify-finalize" disabled={busy || !ready} onClick={() => finalize(false)} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white h-10 px-5 disabled:opacity-50">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" />{ready ? finalizeLabel : `${missing.length} left`}</>}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* PINNED CONTEXT STACK — the teaching note (review) and the dismantler notes freeze
          TOGETHER at the top as one block, stacked with a gap so they never overlap each
          other, and bounded (notes scroll internally) so they never swallow the fields
          being edited. Combining them into ONE sticky wrapper avoids two sticky siblings
          fighting for the same top offset. */}
      {(reviewMode || part.lot) && (
        <div data-testid="identify-pinned-stack" className="sticky top-[64px] z-30 -mx-1 mb-5 space-y-2.5">
          {reviewMode && (
            <div data-testid="review-teach-card" className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2.5 shadow-md">
              <label htmlFor="review-teach-note" className="flex items-center gap-2 text-emerald-800 font-bold text-sm mb-1.5">
                <GraduationCap className="w-4 h-4" />Teaching note <span className="font-normal text-emerald-600">(optional · stays pinned while you review)</span>
              </label>
              <textarea
                id="review-teach-note" data-testid="review-teach-note" value={teachNote}
                onChange={(e) => setTeachNote(e.target.value)} rows={2}
                placeholder="Optional: what should they watch for next time? Reaches the employee with the diff of what you changed."
                className="w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300" />
            </div>
          )}

          {part.lot && (
            <div data-testid="identify-lot-panel">
              <div className="relative bg-white border border-slate-200 rounded-xl overflow-hidden shadow-md">
                <div className="absolute inset-y-0 left-0 w-1 bg-[#E0781A]" aria-hidden />
                <div className="flex items-center gap-2 px-4 py-2.5 pl-5">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-amber-50 text-[#B45309] shrink-0"><StickyNote className="w-3.5 h-3.5" /></span>
                  <h3 className="font-display text-xs font-bold text-slate-700 uppercase tracking-wide shrink-0">Dismantler Notes</h3>
                  <Badge className="rounded-full border-0 text-[10px] font-semibold bg-amber-100 text-amber-800 shrink-0">{part.lot.kind === "legacy" ? "Legacy lot" : "Field lot"}</Badge>
                  <span data-testid="identify-lot-number" className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 shrink-0">{part.lot.batch_number}</span>
                  <span data-testid="identify-lot-title" className="text-sm font-semibold text-slate-800 truncate">{part.lot.title || "Untitled lot"}</span>
                  {part.lot.notes_count > 0 && !showDismantler && (
                    <span data-testid="identify-notes-badge" className="inline-flex items-center gap-1 text-[11px] font-bold text-[#B45309] bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5 shrink-0 animate-pulse">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#E0781A]" />{part.lot.notes_count} note{part.lot.notes_count === 1 ? "" : "s"}
                    </span>
                  )}
                  {part.lot.id && (
                    <Button data-testid="identify-toggle-dismantler" variant="outline" size="sm"
                      onClick={() => setShowDismantler((v) => !v)}
                      className={`ml-auto rounded-lg h-8 text-xs shrink-0 ${part.lot.notes_count > 0 && !showDismantler ? "border-amber-400 text-[#B45309] bg-amber-50 hover:bg-amber-100 font-semibold" : ""}`}>
                      <StickyNote className="w-3.5 h-3.5 mr-1.5" />{showDismantler ? "Hide notes" : (part.lot.notes_count > 0 ? `Show notes (${part.lot.notes_count})` : "Notes")}
                    </Button>
                  )}
                </div>
                {part.lot.id && showDismantler && (
                  <div className="border-t border-slate-100 px-4 pl-5 py-3 max-h-[28dvh] overflow-y-auto" data-testid="identify-dismantler-notes">
                    <NotesLog recordType="lot" recordId={part.lot.id} canAdd={has("edit_lot_notes")} clampLong />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* REWORK BANNER — a returned part carries the admin's feedback (mutually exclusive
          with review mode, so it lives outside the pinned stack). */}
      {part.review_note && part.status !== "pending_review" && (
        <div data-testid="identify-rework-banner" className="-mx-1 mb-5 rounded-xl border-l-4 border-red-500 bg-red-50 px-4 py-3">
          <div className="flex items-center gap-2 text-red-800 font-bold text-sm mb-1"><CornerUpLeft className="w-4 h-4" />Returned for rework{part.returned_by ? ` by ${part.returned_by.split("@")[0]}` : ""}</div>
          <p className="text-sm text-red-900 whitespace-pre-wrap">{part.review_note}</p>
          <p className="text-[11px] text-red-500 mt-1">Fix the above, then complete again to re-submit.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT — large photo workspace (~60%); pinned so photos stay visible while fields scroll */}
        <div className={`lg:col-span-3 sticky ${reviewMode ? (part.lot ? "top-[232px]" : "top-[172px]") : (part.lot ? "top-[124px]" : "top-[64px]")} self-start z-[5] max-h-[calc(100vh-72px)] overflow-y-auto lg:max-h-[calc(100vh-88px)]`}>
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-3"><ImagePlus className="w-4 h-4" />Photos</Label>
            <PhotoViewer photos={photos} uploadEndpoint={`/parts/${part.id}/photos`} onUploaded={addIdentifyPhoto} testidPrefix="identify" uploadLabel="Add photos"
              fixScope="part" fixRecordId={part.id} onFixed={loadPart}
              main={{ scope: "part", recordId: part.id, chosen: part.main_photo_file_id, resolved: part.main_photo?.file_id,
                // only the two main-photo fields change — the fields being typed on this page are never reloaded
                onChanged: (r) => setPart((p) => ({ ...p, main_photo_file_id: r?.main_photo_file_id ?? null, main_photo: r?.main_photo ?? null })) }} />
          </div>
        </div>

        {/* RIGHT — roomy fields (~40%) */}
        <div className="lg:col-span-2 space-y-5">
          {/* ASK AI — top of the flow: identify first, then work down */}
          <AiWorkbench
            partId={part.id}
            photos={photos}
            measurements={part.measurements || []}
            context={{
              year: form.year, make: form.make, model: form.model, engine: form.engine,
              category: (tax.categories || []).find((c) => c.id === form.category_id)?.name || "",
            }}
            tagSerial={form.tag_serial || ""}
            oemNumber={form.oem_number || ""}
            partNumbers={part.numbers || []}
            existingNumbers={Array.from(new Set([
              form.oem_number,
              ...((part.numbers || []).map((n) => n.value)),
              ...(form.superseded ? form.superseded.split(",").map((x) => x.trim()) : []),
            ].filter(Boolean)))}
            onApplySuggestion={applyAiSuggestion}
            onUndoSuggestion={undoAiSuggestion}
            onRemoveKeyword={aiRemoveKeyword}
            onMarketApply={applyMarketPrice}
            onCreateTaxonomy={aiCreateTaxonomy}
            onAddKeyword={aiAddKeywords}
            onNumberAdded={refreshPart}
            canCreateList={canAddListValue}
          />
          <Section title="Vehicle identity">
            {part.donor_id ? (
              <div data-testid="vehicle-locked">
                <div className="flex items-center gap-1.5 mb-3">
                  <span data-testid="identify-donor-locked-badge" className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                    <Lock className="w-3 h-3" />Donor tie: LOCKED · can never change
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-3"><Lock className="w-3.5 h-3.5 text-slate-400" />Locked — anchored to the donor. Edit it on the donor record.</div>
                {/* Line-by-line, full-width rows — shared LockedField, same readable layout as
                    the Part Detail "Source truck" panel, so donor parts look consistent everywhere. */}
                <div className="grid grid-cols-1 gap-4">
                  {[["Year", part.donor?.year, "year", false], ["Make", part.donor?.make, "make", false], ["Model", part.donor?.model, "model", false], ["Engine", form.engine || part.donor?.engine_model, "engine", false], ["VIN", part.donor?.vin, "vin", true]].map(([lbl, val, key, mono]) => (
                    <LockedField key={key} label={lbl} value={val} mono={mono} testid={`identify-donor-${key}`} />
                  ))}
                </div>
              </div>
            ) : (
              <>
                {effectiveMajor && <p className="text-xs text-[#B45309] font-semibold -mt-2 mb-3">Required for major parts</p>}
                <p className="text-[11px] text-slate-400 mb-2">Legacy / no-donor part — pick the vehicle from the lists (add a new one if it's missing).</p>
                <div className="grid grid-cols-1 gap-4">
                  <IdentifyPickField label="Year" testid="identify-year" addNoun="year" placeholder="Year"
                    value={form.year === "" ? "" : String(form.year)} options={yearOpts} canAddNew={canAddListValue}
                    onChange={(v) => setForm((s) => ({ ...s, year: v }))}
                    onAddNew={async (name) => { const { data } = await api.post("/managed-lists/vehicle_years", { name }); loadVehYears(); return data.item.name; }} />
                  <IdentifyPickField label="Make" testid="identify-make" addNoun="make" placeholder="Make"
                    value={form.make} options={makeOpts} canAddNew={canAddListValue}
                    onChange={(v) => setForm((s) => ({ ...s, make: v, model: "", engine: "" }))}
                    onAddNew={async (name) => { const { data } = await api.post("/managed-lists/vehicle_makes", { name }); loadVehMakes(); return data.item.name; }} />
                  <IdentifyPickField label="Model" testid="identify-model" addNoun="model" placeholder="Model"
                    value={form.model} options={modelOpts} canAddNew={canAddListValue}
                    addGuard={() => (!makeId ? "Pick a make first." : null)}
                    addHint={form.make ? `Under "${form.make}"` : "Pick a make first."}
                    onChange={(v) => setForm((s) => ({ ...s, model: v, engine: "" }))}
                    onAddNew={async (name) => { const { data } = await api.post("/managed-lists/vehicle_models", { name, parent_id: makeId }); reloadVehModels(makeId); return data.item.name; }} />
                  <IdentifyPickField label="Engine" testid="identify-engine" addNoun="engine" placeholder="Engine"
                    value={form.engine} options={engineOpts} canAddNew={canAddListValue}
                    addGuard={() => (!modelId ? "Pick a model first." : null)}
                    addHint={form.model ? `Under "${form.model}"` : "Pick a model first."}
                    onChange={(v) => setForm((s) => ({ ...s, engine: v }))}
                    onAddNew={async (name) => { const { data } = await api.post("/managed-lists/vehicle_engines", { name, parent_id: modelId }); reloadVehEngines(modelId); return data.item.name; }} />
                </div>
              </>
            )}
          </Section>

          <Section title="Identity">
            <div className="grid grid-cols-1 gap-4">
              <div className={"rounded-xl" + flashCls("name")}>
              <IdentifyPickField label="Name" required testid="identify-name" addNoun="name" placeholder="Pick or add a name"
                field="name" onNearMiss={logNearMiss}
                value={form.name} options={nameOpts} canAddNew={canAddListValue} headerExtra={aiBadge("name")}
                onChange={(v) => { setForm((s) => ({ ...s, name: v })); clearAiField("name"); }}
                onAddNew={async (name) => { const { data } = await api.post("/managed-lists/names", { name }); loadLists(); return data.item.name; }} />
              </div>
              <div className={"rounded-xl" + flashCls("brand")}>
              <IdentifyPickField label="Brand" required testid="identify-brand" addNoun="brand" placeholder="Pick or add a brand"
                field="brand" onNearMiss={logNearMiss}
                value={form.brand} options={brandOpts} canAddNew={canAddListValue} headerExtra={aiBadge("brand")}
                onChange={(v) => { setForm((s) => ({ ...s, brand: v })); clearAiField("brand"); }}
                onAddNew={async (name) => { const { data } = await api.post("/managed-lists/brands", { name }); loadLists(); return data.item.name; }} />
              </div>
              <div className={"rounded-xl" + flashCls("category")}>
              <IdentifyPickField label="Category" testid="identify-category" addNoun="category" placeholder="—"
                field="category" onNearMiss={logNearMiss}
                value={form.category_id} options={catOpts} canAddNew={canQuickAdd} headerExtra={aiBadge("category")}
                onChange={(v) => { setForm((s) => ({ ...s, category_id: v, part_type_id: "" })); clearAiField("category"); }}
                onAddNew={async (name) => { const { data } = await api.post("/taxonomy/categories", { name }); await loadTax(); return data.id; }} />
              </div>
              <div className={"rounded-xl" + flashCls("part_type")}>
              <IdentifyPickField label="Part type" testid="identify-part-type" addNoun="part type" placeholder="—"
                field="part_type" onNearMiss={logNearMiss}
                value={form.part_type_id} options={typeOpts} canAddNew={canQuickAdd} headerExtra={aiBadge("part_type")}
                addGuard={() => (!form.category_id ? "Pick a category first." : null)}
                addHint={form.category_id ? `Under "${(tax.categories || []).find((c) => c.id === form.category_id)?.name}"` : "Pick a category first."}
                onChange={(v) => { setForm((s) => ({ ...s, part_type_id: v })); clearAiField("part_type"); }}
                onAddNew={async (name) => { const { data } = await api.post("/taxonomy/part-types", { name, category_id: form.category_id }); await loadTax(); return data.id; }} />
              </div>
              <div>
                <div className="flex items-center justify-between min-h-[20px] flex-wrap gap-1"><FieldLabel>OEM number</FieldLabel>{aiBadge("oem_number")}</div>
                <ScanTextInput testid="identify-oem" value={form.oem_number}
                  onChange={(v) => { setForm({ ...form, oem_number: v }); clearAiField("oem_number"); }}
                  className="mt-1.5" inputClassName={"h-11" + flashCls("oem_number")} />
                {unclearLine("oem_number")}
              </div>
              <div>
                <div className="flex items-center justify-between min-h-[20px] flex-wrap gap-1"><FieldLabel>Serial / tag #</FieldLabel>{aiBadge("tag_serial")}</div>
                <ScanTextInput testid="identify-serial" value={form.tag_serial}
                  onChange={(v) => { setForm({ ...form, tag_serial: v }); clearAiField("tag_serial"); }}
                  className="mt-1.5" inputClassName={"h-11" + flashCls("tag_serial")} />
                {unclearLine("tag_serial")}
              </div>
            </div>
            <p data-testid="identify-numbers-source-note" className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-400"><ImagePlus className="w-3.5 h-3.5" />OEM &amp; serial are read from the tag/casting in your photos and entered here — never looked up online.</p>
            <div className="mt-4"><FieldLabel>Superseded numbers (comma-separated)</FieldLabel><Input data-testid="identify-superseded" value={form.superseded} onChange={(e) => setForm({ ...form, superseded: e.target.value })} className="rounded-lg border-slate-200 mt-1.5 h-11" /></div>
            <div className="mt-4">
              <FieldLabel>Keywords / tags</FieldLabel>
              <p className="text-[11px] text-slate-400 mb-1.5">Free tags to make this findable — make, rough year, nicknames. Great for legacy parts with unknown details.</p>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="identify-quick-keywords">
                {["LH", "Left", "RH", "Right", "Front", "Rear", "Upper", "Lower", "Inner", "Outer"].map((t) => {
                  const on = (form.keywords || []).some((k) => k.toLowerCase() === t.toLowerCase());
                  return (
                    <button key={t} type="button" data-testid={`identify-quick-kw-${t.toLowerCase()}`} disabled={on}
                      onClick={() => setForm((s) => (s.keywords || []).some((k) => k.toLowerCase() === t.toLowerCase()) || (s.keywords || []).length >= 25 ? s : { ...s, keywords: [...(s.keywords || []), t] })}
                      className={`inline-flex items-center gap-1 rounded-full text-xs font-semibold px-2.5 py-1 border transition-colors ${on ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-slate-600 border-slate-200 hover:border-[#E0781A] hover:text-[#B45309]"}`}>
                      {on ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}{t}
                    </button>
                  );
                })}
              </div>
              <KeywordChips value={form.keywords} onChange={(kw) => setForm({ ...form, keywords: kw })} testid="identify-keywords" />
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between min-h-[20px] flex-wrap gap-1"><FieldLabel>Description</FieldLabel>{aiBadge("description")}</div>
              <Textarea data-testid="identify-description" value={form.description}
                onChange={(e) => { setForm({ ...form, description: e.target.value }); clearAiField("description"); }}
                placeholder="Internal listing description — what it is, fitment, condition notes for the listing."
                className={"rounded-lg border-slate-200 mt-1.5 text-sm min-h-[90px]" + flashCls("description")} />
            </div>
            <div className="mt-5">
              <PartNumbersEditor partId={part.id} part={part} numbers={part.numbers} needsReconcile={part.numbers_need_reconcile} onReload={refreshPart}
                brandOptions={brandOpts} canAddBrand={canAddListValue}
                onAddBrand={async (name) => { const { data } = await api.post("/managed-lists/brands", { name }); loadLists(); return data.item.name; }}
                onChange={(sp) => setPart((p) => ({ ...p, numbers: sp.numbers, numbers_need_reconcile: sp.numbers_need_reconcile, oem_number: sp.oem_number }))} />
            </div>
            <div className="mt-5">
              <FieldLabel>Measurements</FieldLabel>
              <p className="text-[11px] text-slate-400 mb-1.5 mt-0.5">Physical size + weight (weight drives freight). Human-measured — pick a type, value & unit.</p>
              <MeasurementsEditor partId={part.id} measurements={part.measurements || []}
                onChanged={(sp) => setPart((p) => ({ ...p, measurements: sp.measurements, weight_g: sp.weight_g }))} />
            </div>
          </Section>

          <Section title="Condition">
            {part.source_photo?.file_id && (
              <div data-testid="identify-source-photo" className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-2 mb-4">
                <AuthImg fileId={part.source_photo.file_id} thumb alt="truck photo its numbers were read from" className="w-20 h-20 rounded object-cover shrink-0" />
                <p className="text-xs text-slate-600">Pulled from {part.source_photo.donor_stock || "its truck"}: the truck photo its numbers were read from. A reference only — the part's own photos are taken of the part.</p>
              </div>
            )}
            {/* WHAT THE TRUCK SAID. Somebody crawled under a truck and wrote "engine — blown". If
                the bench call disagrees, that disagreement is the interesting fact: it stays on
                the part with a reason, instead of the earlier call quietly disappearing. */}
            {part.condition_inherited?.state && (
              <div data-testid="identify-inherited-condition"
                className={`rounded-lg border-2 p-3 mb-4 ${part.condition_inherited.state === "present_bad" ? "border-[#B01E28] bg-red-50" : "border-slate-200 bg-slate-50"}`}>
                <div className={`text-xs font-bold uppercase tracking-wider ${part.condition_inherited.state === "present_bad" ? "text-[#B01E28]" : "text-slate-500"}`}>
                  Rated on {part.condition_inherited.donor_stock || "the truck"}: {part.condition_inherited.state === "present_bad" ? "KNOWN BAD" : part.condition_inherited.state === "present_good" ? "GOOD" : part.condition_inherited.state === "gone" ? "GONE" : "PRESENT, UNTESTED"}
                  {part.condition_inherited.reason_label ? ` — ${part.condition_inherited.reason_label}` : ""}
                </div>
                <p className="text-[11px] text-slate-600 mt-1">
                  {part.condition_inherited.by ? `${part.condition_inherited.by}` : "someone"}
                  {part.condition_inherited.at ? ` · ${String(part.condition_inherited.at).slice(0, 10)}` : ""}
                  {part.condition_inherited.note ? ` · “${part.condition_inherited.note}”` : ""}
                  {" · it did not set the grade — that call is yours, in hand."}
                </p>
                {part.condition_inherited.state === "present_bad" && (
                  <div className="mt-2">
                    <FieldLabel accent>Overruling it? Say why</FieldLabel>
                    <Input data-testid="identify-condition-override" value={form.condition_override_reason || ""}
                      onChange={(e) => setForm({ ...form, condition_override_reason: e.target.value })}
                      placeholder="e.g. tested on the stand, runs fine — truck note was the wrong engine"
                      className="rounded-lg border-slate-300 mt-1 h-10" />
                    <p className="text-[10px] text-slate-500 mt-1">Only needed if you grade it A/B/C or drop the known-bad flag. Both readings stay on the part.</p>
                  </div>
                )}
                {part.condition_override?.reason && (
                  <p data-testid="identify-condition-overridden" className="text-[11px] text-amber-700 mt-2">
                    Already overruled by {part.condition_override.by}: “{part.condition_override.reason}”
                  </p>
                )}
              </div>
            )}
            <div><FieldLabel>Condition grade</FieldLabel>
              <Select value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })}>
                <SelectTrigger data-testid="identify-grade" className="rounded-lg border-slate-200 mt-1.5 h-11"><SelectValue placeholder="Select grade" /></SelectTrigger>
                <SelectContent className="rounded-lg">{(constants.grades || []).map((g) => <SelectItem key={g} value={g} data-testid={`identify-grade-opt-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{g}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="mt-4"><FieldLabel>Condition flags</FieldLabel>
              <div className="flex flex-wrap gap-1.5 mt-1.5">{(constants.condition_flags || []).map((f) => <button key={f} type="button" onClick={() => toggleFlag(f)} className={`text-xs font-mono px-2.5 py-1.5 border rounded-lg ${form.condition_flags.includes(f) ? "bg-[#1F1F1F] text-white border-[#1F1F1F]" : "bg-white border-slate-200 text-slate-600"}`}>{f}</button>)}</div>
            </div>
            {effectiveMajor && (
              <label className="mt-4 flex items-center gap-2 text-xs border border-amber-300 bg-amber-50 p-3 rounded-lg"><input data-testid="identify-unverified" type="checkbox" checked={form.unknown_unverified} onChange={(e) => setForm({ ...form, unknown_unverified: e.target.checked })} />Major part: mark unknown / unverified</label>
            )}
          </Section>

          {/* PART NOTES — the stream techs / service writers read; visible by default with an entry box */}
          <Section title="Part Notes">
            <p className="text-[11px] text-slate-400 mb-2">Notes about this part — visible to everyone who looks it up. Permanent, timestamped, attributed.</p>
            <NotesLog recordType="part" recordId={part.id} canAdd={has("manage_parts")} />
          </Section>

          {/* Ask AI moved to the TOP of this column (identify-first flow) */}

          <div data-testid="identify-value-section" className="bg-white border-2 border-slate-200 rounded-2xl overflow-hidden">
            {/* PRIMARY — the load-bearing recovery number */}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-0.5">
                <DollarSign className="w-4 h-4 text-[#B01E28]" />
                <span className="text-sm font-bold text-slate-900">Internal value</span>
                <span className="text-[#B01E28] font-bold">*</span>
                {valNum != null && (() => {
                  const st = priceStamp(displaySource, displayTool);
                  return (
                    <Badge data-testid="identify-value-stamp" className={`ml-auto rounded-full text-[10px] border ${st.cls}`}>
                      {st.icon === "sparkles" && <Sparkles className="w-2.5 h-2.5 mr-0.5" />}
                      {st.icon === "globe" && <Globe className="w-2.5 h-2.5 mr-0.5" />}
                      {st.icon === "check" && <Check className="w-2.5 h-2.5 mr-0.5" />}
                      {st.label}
                    </Badge>
                  );
                })()}
              </div>
              <p className="text-xs text-slate-500 mb-4">Rough internal indicator — the true value is set at the transaction.</p>
              <div className="mb-2">{aiBadge("value")}</div>
              <div className="flex flex-col sm:flex-row gap-3 items-start">
                <div className="relative flex-1 w-full">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-2xl font-display font-bold text-slate-400 pointer-events-none">$</span>
                  <Input data-testid="identify-value" type="number" value={form.internal_value}
                    onChange={(e) => { setForm({ ...form, internal_value: e.target.value }); setValueEdited(true); clearAiField("value"); setValueStamp(null); }}
                    className={"h-14 text-2xl font-display font-bold text-slate-900 pl-9 rounded-xl border-slate-300 w-full focus-visible:ring-[#E0781A]" + flashCls("value")} />
                </div>
                <div className="w-full sm:w-[190px]">
                  <Select value={form.value_basis} onValueChange={(v) => setForm({ ...form, value_basis: v })}>
                    <SelectTrigger data-testid="identify-basis" className="h-14 rounded-xl border-slate-300 text-base"><SelectValue placeholder="Select basis" /></SelectTrigger>
                    <SelectContent className="rounded-lg">
                      <SelectItem value="oem">OEM</SelectItem>
                      <SelectItem value="used">Used-market</SelectItem>
                      <SelectItem value="aftermarket">Aftermarket</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-1 text-center">Basis {fullGate && <span className="text-[#B01E28]">*</span>}</div>
                </div>
              </div>
              {form.value_source === "researched" && !valueEdited ? (
                <div data-testid="identify-researched-signoff" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border bg-emerald-50 text-emerald-800 border-emerald-200">
                  <FlaskConical className="w-3.5 h-3.5" />Researched — signed off{part.price_signoff?.by_name ? ` by ${part.price_signoff.by_name}` : ""}
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button type="button" data-testid="identify-mark-researched" onClick={signOffResearched} disabled={signingOff}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border bg-white text-slate-500 border-slate-200 hover:border-emerald-300 hover:text-emerald-700 transition-colors disabled:opacity-50">
                    <FlaskConical className="w-3.5 h-3.5" />{signingOff ? "Signing off…" : "Sign off as researched"}
                  </button>
                  {!valueEdited && part.price_provenance?.kind === "perplexity" && (
                    <span data-testid="identify-market-source" className="inline-flex items-center gap-1.5 text-xs text-sky-700">
                      <Globe className="w-3.5 h-3.5" />via Perplexity{part.price_provenance?.by_name ? ` · applied by ${part.price_provenance.by_name}` : ""}
                    </span>
                  )}
                </div>
              )}
            </div>
            {/* List price is set at the sale — not enterable here (see money model) */}
          </div>

          {/* READY TO FINALIZE — live checklist */}
          <div data-testid="identify-readiness" className={`bg-white border-2 rounded-2xl p-4 ${ready ? "border-emerald-200" : "border-slate-200"}`}>
            <div className="flex items-center justify-between pb-2.5 mb-2.5 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                {ready ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <ShieldAlert className="w-4 h-4 text-[#E0781A]" />}
                {ready ? "Ready to finalize" : `Ready to finalize — ${missing.length} left`}
              </h3>
              <div className="flex items-center gap-1.5">
                <Badge data-testid="identify-gate-mode" className={`rounded-full border text-[10px] font-mono ${effectiveMajor ? "bg-red-50 text-[#B01E28] border-red-200" : gate.minor_mode === "gated" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                  {effectiveMajor ? "Major — full rigor" : gate.minor_mode === "gated" ? "Gated" : "Fast (light)"}
                </Badge>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              {readinessItems.map((r) => (
                <div key={r.label} data-testid={`readiness-${r.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} className="flex items-center gap-1.5 py-1 text-sm">
                  {r.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
                  <span className={r.ok ? "text-slate-600" : "text-slate-500 font-medium"}>{r.label}</span>
                </div>
              ))}
            </div>
          </div>

          {protectedChange && (
            <div data-testid="identify-reason-wrap" className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
              <FieldLabel accent>Reason for change (required — protected field)</FieldLabel>
              <p className="text-[11px] text-amber-800/80 mb-1.5">You're changing an existing OEM #, supersession #, or value. A reason is logged on the part.</p>
              <Input data-testid="identify-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why is the OEM # / supersession / value changing?" className="rounded-lg border-amber-300 bg-white h-11" />
            </div>
          )}
          <div className="flex gap-3">
            <Button data-testid="identify-save-bottom" variant="outline" disabled={busy} onClick={saveDetails} className="flex-1 rounded-lg h-12">Save details</Button>
            {reviewMode ? (
              <>
                <Button data-testid="review-return-bottom" variant="outline" disabled={busy} onClick={() => { setReviewNote(""); setReviewNoteOpen(true); }} className="flex-1 rounded-lg h-12 border-red-300 text-red-700 hover:bg-red-50"><CornerUpLeft className="w-4 h-4 mr-1.5" />Return</Button>
                <Button data-testid="review-approve-bottom" disabled={busy} onClick={() => approveReview(true)} className="flex-1 rounded-lg h-12 bg-emerald-600 hover:bg-emerald-700 text-white">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" />Fix &amp; approve</>}</Button>
              </>
            ) : canFinalize && (
              <Button data-testid="identify-finalize-bottom" disabled={busy || !ready} onClick={() => finalize(false)} className="flex-1 rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white h-12 disabled:opacity-50">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" />{ready ? (directFinalize ? "Finalize to Available" : "Complete & send to review") : `${missing.length} left to finalize`}</>}
              </Button>
            )}
          </div>
          <AiResearchHistory partId={part.id} />
        </div>
      </div>

      {/* RETURN-FOR-REWORK note (required) — the employee sees exactly what to fix. */}
      <Dialog open={reviewNoteOpen} onOpenChange={setReviewNoteOpen}>
        <DialogContent data-testid="review-return-dialog" className="max-w-md">
          <DialogHeader><DialogTitle>Return for rework</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Enter feedback describing what the employee needs to fix before this part can be approved.</DialogDescription>
          <p className="text-sm text-slate-500 -mt-1">Tell {part.completed_by ? part.completed_by.split("@")[0] : "the employee"} what to fix. This is saved on the part and sent to them.</p>
          <textarea data-testid="review-return-note" value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} rows={4}
            placeholder="e.g. It's a DD15 cooler, not DD13 — check the casting number on the end tank." autoFocus
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-y focus:outline-none focus:border-red-400" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setReviewNoteOpen(false)} className="rounded-lg">Cancel</Button>
            <Button data-testid="review-return-confirm" disabled={busy || !reviewNote.trim()} onClick={returnReview} className="rounded-lg bg-red-600 hover:bg-red-700 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Return for rework"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* RED TWIN VIEW — one tap off the red stamp on OEM # / Serial #. Taking the held number puts
          it in the field; it stays AI-unverified, so the ✓verify chip still has to be tapped. */}
      <RedTwinView partId={part.id} value={twinFor} open={!!twinFor}
        onOpenChange={(v) => !v && setTwinFor(null)}
        onUseTwin={(held) => {
          const field = (form?.oem_number || "").trim() === twinFor ? "oem_number" : "tag_serial";
          setForm((s) => ({ ...s, [field]: held }));
          toast.success(`${held} put in — check it against the stamp, then ✓verify`);
        }} />
      {/* "You have unsaved details — keep editing or discard?" (2026-10-15) */}
      <UnsavedWorkDialog open={askLeave} onOpenChange={setAskLeave} count={1}
        title="You have unsaved details on this part" discardLabel="Leave without saving"
        noun="change"
        onDiscard={() => { setAskLeave(false); savedAt.current = JSON.stringify(form); leaveRef.current?.(); }}
        testid="identify-unsaved" />

    </div>
  );
}
